import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import ShareJobModal from '../../components/ShareJobModal';
import { 
  Plus, 
  MapPin, 
  Calendar, 
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Star,
  Loader2,
  Briefcase,
  Users,
  Search,
  Share2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const JOB_STATUS_OPTIONS = {
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700' },
  em_selecao: { label: 'Em Seleção', color: 'bg-amber-100 text-amber-700' },
  finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' }
};

export default function CompanyJobs() {
  const navigate = useNavigate();
  const { selectedCompany } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteJob, setDeleteJob] = useState(null);
  const [shareJob, setShareJob] = useState(null);

  // Recarrega dados quando a empresa selecionada mudar
  useEffect(() => {
    setLoading(true);
    fetchJobs();
  }, [selectedCompany?.company_id]);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.getMy();
      setJobs(response.data);
    } catch (error) {
      toast.error('Erro ao carregar vagas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteJob) return;
    try {
      await jobsAPI.delete(deleteJob.id);
      toast.success('Vaga excluída com sucesso');
      setJobs(jobs.filter(j => j.id !== deleteJob.id));
    } catch (error) {
      toast.error('Erro ao excluir vaga');
    } finally {
      setDeleteJob(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="company-jobs">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Minhas Vagas</h1>
            <p className="text-slate-600">Gerencie suas oportunidades</p>
          </div>
          <Button 
            onClick={() => navigate('/company/jobs/new')}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="new-job-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Vaga
          </Button>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : jobs.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Nenhuma vaga cadastrada
              </h3>
              <p className="text-slate-600 mb-6">
                Crie sua primeira vaga e comece a receber candidaturas
              </p>
              <Button 
                onClick={() => navigate('/company/jobs/new')}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Vaga
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card 
                key={job.id} 
                className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/company/jobs/${job.id}/candidates`)}
                data-testid={`job-item-${job.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {job.destaque && (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                            <Star className="w-3 h-3 mr-1" />
                            Destaque
                          </Badge>
                        )}
                        <Badge variant={job.publica ? 'default' : 'secondary'}>
                          {job.publica ? 'Pública' : 'Privada'}
                        </Badge>
                        <Badge variant="outline">{job.tipo_contrato}</Badge>
                        {job.status && JOB_STATUS_OPTIONS[job.status] && (
                          <Badge className={JOB_STATUS_OPTIONS[job.status].color}>
                            {JOB_STATUS_OPTIONS[job.status].label}
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        {job.titulo}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.localizacao}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(job.created_at)}
                        </span>
                      </div>
                      <p className="text-slate-600 line-clamp-2">
                        {job.descricao}
                      </p>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/company/jobs/${job.id}/candidates`)}
                        className="border-slate-400 text-slate-700 hover:bg-slate-100"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Candidatos
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => setShareJob(job)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Share2 className="w-4 h-4 mr-1" />
                        Compartilhar
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/company/jobs/${job.id}/candidates`)}>
                            <Users className="w-4 h-4 mr-2" />
                            Gerenciar Candidatos
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/company/jobs/${job.id}/applications`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Candidaturas
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/company/jobs/${job.id}/edit`)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeleteJob(job)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteJob} onOpenChange={() => setDeleteJob(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Vaga</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir a vaga "{deleteJob?.titulo}"? 
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Share Job Modal */}
        <ShareJobModal 
          open={!!shareJob} 
          onOpenChange={(open) => !open && setShareJob(null)}
          job={shareJob}
        />
      </div>
    </DashboardLayout>
  );
}
